import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logActivity } from './useActivityLog';
import { createRealtimeChannelName, logRealtimeIssue, removeRealtimeChannel } from '@/lib/realtime';

export type BedCountStatus = 'pending' | 'in_progress' | 'awaiting_signoff' | 'completed';
export type BedCountResult = 'present' | 'not_present' | 'unbedded' | 'pass_acknowledged';

export interface BedCount {
  id: string;
  site_id: string;
  scheduled_for: string;
  started_at: string | null;
  started_by: string | null;
  signed_off_at: string | null;
  signed_off_by: string | null;
  status: BedCountStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // AVC report fields (added with DHS compliance work)
  offline_beds?: number;
  reported_emergencies?: number;
  called_in_at?: string | null;
  called_in_by?: string | null;
  avc_report_notes?: string | null;
}

export interface BedCountEntry {
  id: string;
  bed_count_id: string;
  client_id: string;
  room_id: string | null;
  expected_status: string | null;
  result: BedCountResult | null;
  marked_by: string | null;
  marked_at: string | null;
  notes: string | null;
}

/**
 * Fetch the most recent active (non-completed) bed count for a site.
 */
export const useActiveBedCount = (siteId?: string) => {
  const qc = useQueryClient();

  useEffect(() => {
    if (!siteId) return;
    try {
      const channel = supabase
        .channel(createRealtimeChannelName(`bed-counts-${siteId}`))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bed_counts', filter: `site_id=eq.${siteId}` }, () => {
          qc.invalidateQueries({ queryKey: ['bed-count', 'active', siteId] });
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bed_count_entries' }, () => {
          qc.invalidateQueries({ queryKey: ['bed-count', 'entries'] });
        })
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Bed counts', error);
        });
      return () => { removeRealtimeChannel(channel); };
    } catch (error) {
      logRealtimeIssue('Bed counts', error);
    }
  }, [siteId, qc]);

  return useQuery({
    queryKey: ['bed-count', 'active', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bed_counts')
        .select('*')
        .eq('site_id', siteId!)
        .neq('status', 'completed')
        .order('scheduled_for', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as BedCount | null;
    },
  });
};

export const useBedCountEntries = (bedCountId?: string) => {
  const qc = useQueryClient();

  useEffect(() => {
    if (!bedCountId) return;
    try {
      const channel = supabase
        .channel(createRealtimeChannelName(`bed-count-entries-${bedCountId}`))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bed_count_entries', filter: `bed_count_id=eq.${bedCountId}` }, () => {
          qc.invalidateQueries({ queryKey: ['bed-count', 'entries', bedCountId] });
          qc.invalidateQueries({ queryKey: ['bed-counts'] });
        })
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Bed count entries', error);
        });
      return () => { removeRealtimeChannel(channel); };
    } catch (error) {
      logRealtimeIssue('Bed count entries', error);
    }
  }, [bedCountId, qc]);

  return useQuery({
    queryKey: ['bed-count', 'entries', bedCountId],
    enabled: !!bedCountId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bed_count_entries')
        .select('*')
        .eq('bed_count_id', bedCountId!);
      if (error) throw error;
      return (data ?? []) as BedCountEntry[];
    },
  });
};

/**
 * Start a bed count: pre-populate entries from current bedded clients at the site.
 */
export const useStartBedCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bedCountId, siteId }: { bedCountId: string; siteId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Pull every client with an assigned bed at this facility
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, room_id, status, pass_start, pass_end')
        .eq('site_id', siteId)
        .is('archived_at', null)
        .not('room_id', 'is', null);
      if (clientsError) throw clientsError;

      const now = new Date();
      const nowIso = now.toISOString();
      const entries = (clients ?? []).map((c: any) => {
        const onPass =
          c.pass_start && c.pass_end &&
          new Date(c.pass_start) <= now && new Date(c.pass_end) >= now;
        const expected_status = onPass ? 'on_pass' : (c.status ?? 'checked-in');
        // Auto-seed result based on current census status
        let result: BedCountResult;
        if (onPass) result = 'pass_acknowledged';
        else if (c.status === 'checked-in') result = 'present';
        else result = 'not_present';
        return {
          bed_count_id: bedCountId,
          client_id: c.id,
          room_id: c.room_id,
          expected_status,
          result,
          marked_by: user.id,
          marked_at: nowIso,
          notes: 'auto-seeded from census',
        };
      });

      if (entries.length > 0) {
        const { error: insertError } = await supabase
          .from('bed_count_entries')
          .insert(entries);
        if (insertError) throw insertError;
      }

      const { error: updateError } = await supabase
        .from('bed_counts')
        .update({
          status: 'in_progress',
          started_at: now.toISOString(),
          started_by: user.id,
        })
        .eq('id', bedCountId);
      if (updateError) throw updateError;

      await logActivity({
        actionType: 'bed_count' as any,
        actionDetails: { event: 'bed_count_start', bed_count_id: bedCountId, entry_count: entries.length },
        siteId,
      });

      return { bedCountId, entryCount: entries.length };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bed-count'] });
      qc.invalidateQueries({ queryKey: ['bed-counts'] });
    },
  });
};

export const useMarkBedCountEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entryId,
      result,
      notes,
    }: { entryId: string; result: BedCountResult; notes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('bed_count_entries')
        .update({
          result,
          notes: notes ?? null,
          marked_by: user.id,
          marked_at: new Date().toISOString(),
        })
        .eq('id', entryId);
      if (error) throw error;

      // Census status flip is deferred until the count is signed off.
      // Individual bed checks stay in bed_count_entries; the Shift Timeline only shows count start/completion summaries.
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bed-count'] });
      qc.invalidateQueries({ queryKey: ['bed-count', 'entries'] });
      qc.invalidateQueries({ queryKey: ['bed-counts'] });
    },
  });
};

export const useRequestSignOff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bedCountId: string) => {
      const { error } = await supabase
        .from('bed_counts')
        .update({ status: 'awaiting_signoff' })
        .eq('id', bedCountId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bed-count'] });
      qc.invalidateQueries({ queryKey: ['bed-counts'] });
    },
  });
};

export const useSignOffBedCount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bedCountId, siteId }: { bedCountId: string; siteId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Client-side gate; server-side enforcement should be added in a future migration via trigger.
      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      const allowed = (roleRow ?? []).some((r: any) =>
        ['super_admin', 'program_director', 'manager', 'supervisor'].includes(r.role)
      );
      if (!allowed) throw new Error('Sign-off requires Supervisor or above');

      const signedOffAt = new Date().toISOString();

      const { error } = await supabase
        .from('bed_counts')
        .update({
          status: 'completed',
          signed_off_at: signedOffAt,
          signed_off_by: user.id,
        })
        .eq('id', bedCountId);
      if (error) throw error;

      // Flip census status for everyone marked Not Present in this count
      const { data: notPresentEntries } = await supabase
        .from('bed_count_entries')
        .select('client_id')
        .eq('bed_count_id', bedCountId)
        .eq('result', 'not_present');

      const clientIds = (notPresentEntries ?? [])
        .map((e: any) => e.client_id)
        .filter(Boolean);

      if (clientIds.length > 0) {
        await supabase
          .from('clients')
          .update({ status: 'not_present', not_present_at: signedOffAt })
          .in('id', clientIds);
      }

      await logActivity({
        actionType: 'bed_count' as any,
        actionDetails: {
          event: 'bed_count_signoff',
          bed_count_id: bedCountId,
          not_present_count: clientIds.length,
        },
        siteId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bed-count'] });
      qc.invalidateQueries({ queryKey: ['bed-counts'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['rosters'] });
    },
  });
};
